import React, { createContext, use, useState } from 'react'
import { CreditDocument } from '_gql'

type FileUploadState = 'loading' | 'success' | 'failure'

interface UploadRequest {
  salesforceCreditId: string
  documentUuid: string
  documentType: CreditDocument
  fileName: string
}

interface Upload extends UploadRequest {
  state: FileUploadState
}

export interface FileUploadStatusContextValue {
  addNewFileUpload: (request: UploadRequest) => void
  getFileUpload: (uuid: string) => Upload | null
  updateFileUploadState: (uuid: string, state: FileUploadState) => void
  removeFileUpload: (uuid: string) => void
}

const FileUploadStatusContext = createContext<FileUploadStatusContextValue>({
  addNewFileUpload: () => {},
  getFileUpload: () => {
    return null
  },
  updateFileUploadState: () => {},
  removeFileUpload: () => {},
})

const FileUploadStatusProvider = ({ children }: React.PropsWithChildren) => {
  const [fileUploads, setFileUploads] = useState<Upload[]>([])

  const addNewFileUpload = (request: UploadRequest) => {
    setFileUploads((prev) => {
      return [
        {
          ...request,
          state: 'loading',
        },
        ...prev,
      ]
    })
  }

  const updateFileUploadState = (requestedUuid: string, state: FileUploadState) => {
    setFileUploads((prev) => {
      const indexOfItem = prev.findIndex(({ documentUuid }) => documentUuid === requestedUuid)
      if (indexOfItem < 0) {
        return prev
      }

      return [
        ...prev.slice(0, indexOfItem),
        {
          ...prev[indexOfItem],
          state,
        },
        ...prev.slice(indexOfItem + 1),
      ]
    })
  }

  const getFileUpload = (requestedUuid: string) => {
    return fileUploads.find(({ documentUuid }) => documentUuid === requestedUuid) ?? null
  }

  const removeFileUpload = (requestedUuid: string) => {
    setFileUploads((prev) => [...prev.filter(({ documentUuid }) => documentUuid !== requestedUuid)])
  }

  return (
    <FileUploadStatusContext
      value={{
        addNewFileUpload,
        getFileUpload,
        updateFileUploadState,
        removeFileUpload,
      }}
    >
      {children}
    </FileUploadStatusContext>
  )
}

export const useFileUploadStatus = () => {
  const context = use(FileUploadStatusContext)
  if (!context) {
    throw new Error('useFileUploadStatus must be used inside a FileUploadStatusProvider')
  }

  return context
}

export default FileUploadStatusProvider
